import { useCallback, useEffect, useState } from 'react';
import { BACKEND_URL } from '../config';
import type { Session } from '../types/app';
import type { BetaWelcomeContent } from '../types/beta-welcome';
import { DEFAULT_BETA_WELCOME_CONTENT } from '../types/beta-welcome';
import BetaWelcomeModal from './BetaWelcomeModal';
import {
  BetaAdminActions,
  BetaAdminCard,
  BetaAdminField,
  betaAdminInputProps,
  betaAdminTextareaProps,
} from './BetaAdminUi';

function adminFetch(url: string, pseudo: string, options: RequestInit = {}): Promise<Response> {
  return fetch(url, {
    ...options,
    headers: {
      ...(options.headers || {}),
      'X-Admin-Pseudo': pseudo,
    },
  });
}

function linesToText(items: string[]): string {
  return items.join('\n');
}

function textToLines(text: string): string[] {
  return text.split('\n').map(line => line.trim()).filter(Boolean);
}

interface BetaWelcomeAdminProps {
  session: Session;
  knownIssuesPreview: string[];
  busy: boolean;
  onBusyChange: (busy: boolean) => void;
  onFeedback: (message: string) => void;
}

export default function BetaWelcomeAdmin({
  session,
  knownIssuesPreview,
  busy,
  onBusyChange,
  onFeedback,
}: BetaWelcomeAdminProps) {
  const [content, setContent] = useState<BetaWelcomeContent>(DEFAULT_BETA_WELCOME_CONTENT);
  const [readyText, setReadyText] = useState('');
  const [unstableText, setUnstableText] = useState('');
  const [loading, setLoading] = useState(true);
  const [previewOpen, setPreviewOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/system/beta/welcome-content?pseudo=${encodeURIComponent(session.pseudo)}`,
        session.pseudo,
      );
      if (!res.ok) throw new Error('Chargement impossible');
      const data = await res.json() as { content: BetaWelcomeContent };
      setContent(data.content);
      setReadyText(linesToText(data.content.readyItems));
      setUnstableText(linesToText(data.content.unstableItems));
    } catch (err) {
      onFeedback(err instanceof Error ? err.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [onFeedback, session.pseudo]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    onBusyChange(true);
    try {
      const payload: BetaWelcomeContent = {
        ...content,
        readyItems: textToLines(readyText),
        unstableItems: textToLines(unstableText),
      };
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/system/beta/welcome-content?pseudo=${encodeURIComponent(session.pseudo)}`,
        session.pseudo,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur');
      setContent(data.content);
      setReadyText(linesToText(data.content.readyItems));
      setUnstableText(linesToText(data.content.unstableItems));
      onFeedback('Message de bienvenue enregistré');
    } catch (err) {
      onFeedback(err instanceof Error ? err.message : 'Erreur');
    } finally {
      onBusyChange(false);
    }
  };

  const reset = async () => {
    onBusyChange(true);
    try {
      const res = await adminFetch(
        `${BACKEND_URL}/api/admin/system/beta/welcome-content/reset?pseudo=${encodeURIComponent(session.pseudo)}`,
        session.pseudo,
        { method: 'POST' },
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Erreur');
      setContent(data.content);
      setReadyText(linesToText(data.content.readyItems));
      setUnstableText(linesToText(data.content.unstableItems));
      onFeedback('Contenu par défaut restauré');
    } catch (err) {
      onFeedback(err instanceof Error ? err.message : 'Erreur');
    } finally {
      onBusyChange(false);
    }
  };

  const previewContent: BetaWelcomeContent = {
    ...content,
    readyItems: textToLines(readyText),
    unstableItems: textToLines(unstableText),
  };

  const disabled = busy || loading;

  return (
    <BetaAdminCard
      icon="👋"
      title="Modal de bienvenue"
      description="Texte affiché à la première connexion (hors problèmes connus, gérés à part)."
      footer={(
        <BetaAdminActions>
          <button
            type="button"
            className="beta-admin-btn beta-admin-btn--primary"
            disabled={disabled}
            onClick={() => void save()}
          >
            Enregistrer
          </button>
          <button
            type="button"
            className="beta-admin-btn"
            disabled={disabled}
            onClick={() => setPreviewOpen(true)}
          >
            Prévisualiser
          </button>
          <button
            type="button"
            className="beta-admin-btn beta-admin-btn--ghost"
            disabled={disabled}
            onClick={() => void reset()}
          >
            Restaurer défaut
          </button>
        </BetaAdminActions>
      )}
    >
      <div className="beta-admin-form-grid">
        <BetaAdminField label="Titre" htmlFor="beta-welcome-title" full>
          <input
            id="beta-welcome-title"
            type="text"
            {...betaAdminInputProps(disabled)}
            value={content.title}
            onChange={e => setContent(c => ({ ...c, title: e.target.value }))}
          />
        </BetaAdminField>

        <BetaAdminField label="Introduction" htmlFor="beta-welcome-lead" full>
          <textarea
            id="beta-welcome-lead"
            {...betaAdminTextareaProps(disabled, 2)}
            value={content.lead}
            onChange={e => setContent(c => ({ ...c, lead: e.target.value }))}
          />
        </BetaAdminField>

        <BetaAdminField
          label="Ce qui est prêt"
          hint="Une ligne par point"
          htmlFor="beta-welcome-ready"
        >
          <textarea
            id="beta-welcome-ready"
            {...betaAdminTextareaProps(disabled, 5)}
            value={readyText}
            onChange={e => setReadyText(e.target.value)}
          />
        </BetaAdminField>

        <BetaAdminField
          label="Encore instable"
          hint="Une ligne par point"
          htmlFor="beta-welcome-unstable"
        >
          <textarea
            id="beta-welcome-unstable"
            {...betaAdminTextareaProps(disabled, 5)}
            value={unstableText}
            onChange={e => setUnstableText(e.target.value)}
          />
        </BetaAdminField>

        <BetaAdminField label="Titre section retours" htmlFor="beta-welcome-feedback-title" full>
          <input
            id="beta-welcome-feedback-title"
            type="text"
            {...betaAdminInputProps(disabled)}
            value={content.feedbackTitle}
            onChange={e => setContent(c => ({ ...c, feedbackTitle: e.target.value }))}
          />
        </BetaAdminField>

        <BetaAdminField label="Texte section retours" htmlFor="beta-welcome-feedback-text" full>
          <textarea
            id="beta-welcome-feedback-text"
            {...betaAdminTextareaProps(disabled, 3)}
            value={content.feedbackText}
            onChange={e => setContent(c => ({ ...c, feedbackText: e.target.value }))}
          />
        </BetaAdminField>
      </div>

      {previewOpen && (
        <BetaWelcomeModal
          forceOpen
          previewMode
          previewContent={previewContent}
          previewKnownIssues={knownIssuesPreview}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </BetaAdminCard>
  );
}
